import { BASE_URL } from "../data/constants";
import { getToken } from "./authStore";

/**
 * Ett fel från API:et, i det format apiErrors.js på servern skickar.
 *
 * Tidigare kastades ett vanligt Error med rå responstext i meddelandet.
 * Formulären hade då inget annat val än att skriva "Something went wrong"
 * under hela formuläret, oavsett om det var e-posten som var upptagen
 * eller lösenordet som var för kort.
 *
 *   { error: "validation_failed",
 *     message: "Please check the fields below.",
 *     fields: { password: "password_too_short" } }
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string>;

  constructor(status: number, code: string, message: string, fields: Record<string, string> = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fields = fields;
  }

  /** Felkoden för ett visst fält, om servern pekade ut det. */
  fieldError(name: string): string | undefined {
    return this.fields[name];
  }
}

/**
 * Läsbar text per felkod.
 *
 * Servern skickar med en `message`, men klienten översätter själv där den
 * kan säga något mer precist i sitt sammanhang. Okända koder faller
 * tillbaka på serverns text.
 */
const MESSAGES: Record<string, string> = {
  email_invalid: "Enter a valid email address.",
  email_taken: "That email address is already in use.",
  email_unchanged: "That is already your email address.",
  missing_credentials: "Fill in both fields.",
  invalid_credentials: "That email and password combination did not work.",
  password_required: "Enter your password.",
  password_too_short: "Use at least 8 characters.",
  password_too_long: "That password is too long.",
  password_too_weak: "Use at least one letter and one number.",
  password_incorrect: "That is not your current password.",
  terms_required: "You need to accept the terms to continue.",
  terms_version_mismatch: "The terms have changed. Reload the page and try again.",
  year_of_birth_invalid: "Choose your year of birth.",
  birth_date_required: "Enter your date of birth.",
  birth_date_invalid: "Enter a valid date of birth.",
  too_young: "You need to be at least 18 to use Beatspot.",
  legal_unavailable:
    "We could not load the current terms. Reload the page and try again.",
  name_too_long: "That name is too long.",
  bio_too_long: "Keep your bio under 280 characters.",
  invalid_token: "That link is no longer valid. Request a new one.",
  mail_not_configured: "Email is not set up on this server yet.",
  mail_send_failed: "We could not send that email. Try again shortly.",
  no_password_account: "This account does not sign in with a password.",
  rate_limited: "Too many attempts. Wait a while and try again.",
  network_error: "Could not reach Beatspot. Check your connection.",
};

export function messageForCode(code: string | undefined, fallback = "Something went wrong."): string {
  if (!code) return fallback;
  return MESSAGES[code] ?? fallback;
}

/** Texten som hör till ett fältfel, redo att visas under en input. */
export function fieldMessage(error: unknown, name: string): string | undefined {
  if (!(error instanceof ApiError)) return undefined;

  const code = error.fieldError(name);
  return code ? messageForCode(code) : undefined;
}

/**
 * Texten för formulärets sammanfattande fel.
 *
 * Pekar felet ut enskilda fält visas ingen sammanfattning — den skulle
 * bara upprepa det som redan står under fälten.
 */
export function formMessage(error: unknown): string | undefined {
  if (error instanceof ApiError) {
    if (Object.keys(error.fields).length > 0) return undefined;
    return messageForCode(error.code, error.message);
  }

  if (error) return messageForCode("network_error");
  return undefined;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();

  let res: Response;

  try {
    res = await fetch(`${BASE_URL}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
  } catch {
    // Nätverksfel har ingen statuskod. Utan det här fallet kastades ett
    // TypeError som formulären inte kunde skilja från ett serverfel.
    throw new ApiError(0, "network_error", messageForCode("network_error"));
  }

  if (!res.ok) {
    const text = await res.text();

    let parsed: { error?: string; message?: string; fields?: Record<string, string> } | null = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = null;
    }

    const code = parsed?.error ?? `http_${res.status}`;

    throw new ApiError(
      res.status,
      code,
      parsed?.message ?? messageForCode(code, res.statusText || "Request failed"),
      parsed?.fields ?? {}
    );
  }

  // 202 och 204 kan sakna kropp. res.json() på en tom kropp kastar.
  const raw = await res.text();
  return (raw ? JSON.parse(raw) : null) as T;
}

export const http = {
  get: <T>(path: string) => request<T>(path),

  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),

  put: <T>(path: string, body: unknown) =>
    request<T>(path, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "DELETE",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),

  patch: <T>(path: string, body: unknown) =>
    request<T>(path, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
};
